import { useState, useCallback } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { ROUTES, buildProviderSessionNotesPath } from "@/routes";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Users, Shield } from "lucide-react";
import { VideoPlaceholder, VideoControls, DeviceSelections } from "@/components/telehealth/VideoPlaceholder";
import { SessionTimer } from "@/components/telehealth/SessionTimer";
import { SessionChat } from "@/components/telehealth/SessionChat";
import { SpeechNoteCapture } from "@/components/telehealth/SpeechNoteCapture";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface SOAPNotes {
  chief_complaint: string;
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
}

export default function TelehealthSession() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { sessionId } = useParams();
  const { user } = useAuth();
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [currentSoapNotes, setCurrentSoapNotes] = useState<SOAPNotes | null>(null);
  const [deviceSelections, setDeviceSelections] = useState<DeviceSelections>({
    microphoneId: "default",
    cameraId: "default",
    speakerId: "default",
  });

  // Fetch session details from DB when sessionId is available
  const { data: sessionData } = useQuery({
    queryKey: ["telehealth-session", sessionId],
    queryFn: async () => {
      const { data: session, error } = await supabase
        .from("telehealth_sessions")
        .select("*")
        .eq("id", sessionId!)
        .single();
      if (error) throw error;

      // Get member name from profiles or intake forms
      const { data: profile } = await supabase
        .from("profiles")
        .select("display_name, email")
        .eq("id", session.member_id)
        .single();

      const { data: intake } = await supabase
        .from("patients")
        .select("first_name, last_name")
        .eq("user_id", session.member_id)
        .maybeSingle();

      const memberName = intake
        ? `${intake.first_name} ${intake.last_name}`
        : profile?.display_name || profile?.email || "Patient";

      return { ...session, member_name: memberName };
    },
    enabled: !!sessionId && sessionId !== "new",
  });

  // Get patient info from URL params or session data
  const patientId = searchParams.get("patientId") || sessionData?.member_id || undefined;
  const participantName = searchParams.get("patientName") || sessionData?.member_name || "Patient";
  const localName = user?.email?.split("@")[0] || "Provider";

  const handleStartSession = () => {
    setIsSessionActive(true);
    setSessionStartTime(new Date());
    setTimeout(() => setIsConnected(true), 2000);
  };

  const handlePauseSession = () => {
    setIsSessionActive(false);
  };

  const handleEndSession = () => {
    setShowEndDialog(true);
  };

  const confirmEndSession = () => {
    setIsSessionActive(false);
    setShowEndDialog(false);
    // Navigate to session documentation with SOAP notes pre-filled
    const sid = sessionId || searchParams.get("sessionId") || "new";
    navigate(buildProviderSessionNotesPath(sid), {
      state: {
        soapNotes: currentSoapNotes,
        patientName: participantName,
        duration: sessionStartTime
          ? Math.round((Date.now() - sessionStartTime.getTime()) / 60000)
          : 0,
      },
    });
  };

  const handleDeviceChange = useCallback((devices: DeviceSelections) => {
    setDeviceSelections(devices);
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-card px-4 py-3">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="h-6 w-px bg-border" />
            <div>
              <h1 className="text-lg font-semibold text-foreground">Telehealth Session</h1>
              <p className="text-sm text-muted-foreground">with {participantName}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="gap-1">
              <Shield className="h-3 w-3" />
              HIPAA Compliant
            </Badge>
            <Badge variant="secondary" className="gap-1">
              <Users className="h-3 w-3" />
              2 Participants
            </Badge>
          </div>
        </div>
      </header>

      {/* Timer */}
      <div className="px-4 py-3 border-b border-border bg-muted/50">
        <div className="max-w-7xl mx-auto">
          <SessionTimer
            isActive={isSessionActive}
            startTime={sessionStartTime}
            onStart={handleStartSession}
            onPause={handlePauseSession}
            onEnd={handleEndSession}
          />
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 p-4">
        <div className="max-w-7xl mx-auto h-full">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 h-full">
            {/* Video area */}
            <div className="lg:col-span-3 space-y-4">
              {/* Main video (remote participant) */}
              <VideoPlaceholder
                participantName={participantName}
                isConnected={isConnected}
              />

              {/* Local video + Speech notes */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="col-span-1">
                  <VideoPlaceholder
                    participantName={localName}
                    isLocal
                    isConnected={isConnected}
                  />
                </div>
                <div className="col-span-1 md:col-span-3">
                  <SpeechNoteCapture
                    patientId={patientId}
                    patientName={participantName}
                    sessionActive={isSessionActive}
                    microphoneId={deviceSelections.microphoneId}
                    onSoapNotesChange={setCurrentSoapNotes}
                  />
                </div>
              </div>
            </div>

            {/* Chat sidebar */}
            <div className="lg:col-span-1 h-[500px] lg:h-auto">
              <SessionChat participantName={participantName} localName={localName} />
            </div>
          </div>
        </div>
      </div>

      {/* Video controls with device settings */}
      <VideoControls onEndCall={handleEndSession} onDeviceChange={handleDeviceChange} />

      {/* End session confirmation dialog */}
      <AlertDialog open={showEndDialog} onOpenChange={setShowEndDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End Telehealth Session?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to end this session? The session duration and billing units will be recorded. You will be redirected to complete post-session documentation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Session</AlertDialogCancel>
            <AlertDialogAction onClick={confirmEndSession}>
              End Session
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
